/**
 * Extrai um frame de um vídeo (arquivo local ou URL remota com CORS)
 * para thumbnail / capa. Requer crossOrigin anônimo em URLs remotas.
 */

export type ExtractVideoFrameOptions = {
  /** Tempo em segundos (default: ~10% da duração, máx 1s). */
  timeSeconds?: number;
  /** Qualidade JPEG 0–1 (default 0.85). */
  quality?: number;
  /** Largura máxima do JPEG (default 1280). */
  maxWidth?: number;
  fileName?: string;
};

export type ExtractedVideoFrame = {
  blob: Blob;
  file: File;
  objectUrl: string;
  width: number;
  height: number;
  timeSeconds: number;
};

/** Cache em memória de frames gerados no client (por URL do vídeo). */
const frameCache = new Map<string, string>();
const frameInflight = new Map<string, Promise<string>>();

export function getCachedVideoFrameUrl(videoUrl: string): string | null {
  return frameCache.get(videoUrl) ?? null;
}

function loadVideoElement(source: File | string): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    let objectUrl: string | null = null;

    const cleanupListeners = () => {
      video.onloadedmetadata = null;
      video.onerror = null;
    };

    video.onerror = () => {
      cleanupListeners();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      reject(new Error('Não foi possível carregar o vídeo.'));
    };

    video.onloadedmetadata = () => {
      cleanupListeners();
      resolve(video);
    };

    if (typeof source === 'string') {
      video.crossOrigin = 'anonymous';
      video.src = source;
    } else {
      objectUrl = URL.createObjectURL(source);
      video.src = objectUrl;
    }

    if (objectUrl) {
      (video as HTMLVideoElement & { __blobUrl?: string }).__blobUrl = objectUrl;
    }

    video.load();
  });
}

function seekVideo(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      resolve();
    };
    const onError = () => {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      reject(new Error('Não foi possível posicionar o vídeo.'));
    };

    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const safeTime =
      duration > 0 ? Math.min(Math.max(0, time), Math.max(0, duration - 0.05)) : Math.max(0, time);

    if (Math.abs(video.currentTime - safeTime) < 0.01 && video.readyState >= 2) {
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      resolve();
      return;
    }

    video.currentTime = safeTime;
  });
}

function defaultSeekTime(duration: number): number {
  if (!Number.isFinite(duration) || duration <= 0) return 0.1;
  return Math.min(1, duration * 0.1);
}

function drawFrameToBlob(
  video: HTMLVideoElement,
  quality: number,
  maxWidth: number,
): Promise<{ blob: Blob; width: number; height: number }> {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) {
    return Promise.reject(new Error('Vídeo sem dimensões válidas para captura.'));
  }

  const scale = vw > maxWidth ? maxWidth / vw : 1;
  const width = Math.round(vw * scale);
  const height = Math.round(vh * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return Promise.reject(new Error('Não foi possível preparar a captura do frame.'));
  }

  ctx.drawImage(video, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Falha ao gerar a imagem do frame.'));
          return;
        }
        resolve({ blob, width, height });
      },
      'image/jpeg',
      quality,
    );
  });
}

function revokeVideoBlobUrl(video: HTMLVideoElement) {
  const blobUrl = (video as HTMLVideoElement & { __blobUrl?: string }).__blobUrl;
  if (blobUrl) {
    URL.revokeObjectURL(blobUrl);
  }
  video.removeAttribute('src');
  video.load();
}

export async function extractVideoFrame(
  source: File | string,
  options: ExtractVideoFrameOptions = {},
): Promise<ExtractedVideoFrame> {
  const quality = options.quality ?? 0.85;
  const maxWidth = options.maxWidth ?? 1280;
  const fileName = options.fileName ?? 'video-frame.jpg';

  const video = await loadVideoElement(source);

  try {
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const timeSeconds =
      options.timeSeconds !== undefined ? options.timeSeconds : defaultSeekTime(duration);

    await seekVideo(video, timeSeconds);
    const { blob, width, height } = await drawFrameToBlob(video, quality, maxWidth);
    const file = new File([blob], fileName, { type: 'image/jpeg' });
    const objectUrl = URL.createObjectURL(blob);

    return { blob, file, objectUrl, width, height, timeSeconds };
  } finally {
    revokeVideoBlobUrl(video);
  }
}

/**
 * Extrai (ou reutiliza do cache) um frame JPEG leve para preview na grade.
 * Não revoga o object URL — fica em cache enquanto a aba estiver aberta.
 */
export async function getOrExtractVideoPreviewUrl(videoUrl: string): Promise<string> {
  const cached = frameCache.get(videoUrl);
  if (cached) return cached;

  const inflight = frameInflight.get(videoUrl);
  if (inflight) return inflight;

  const promise = extractVideoFrame(videoUrl, {
    maxWidth: 480,
    quality: 0.72,
    fileName: 'video-preview.jpg',
  })
    .then((frame) => {
      frameCache.set(videoUrl, frame.objectUrl);
      frameInflight.delete(videoUrl);
      return frame.objectUrl;
    })
    .catch((err) => {
      frameInflight.delete(videoUrl);
      throw err;
    });

  frameInflight.set(videoUrl, promise);
  return promise;
}

/** Thumbnail leve para enviar no upload de vídeo (~640px). */
export async function extractVideoThumbnailFile(file: File): Promise<File> {
  const frame = await extractVideoFrame(file, {
    maxWidth: 640,
    quality: 0.8,
    fileName: 'video-thumb.jpg',
  });
  URL.revokeObjectURL(frame.objectUrl);
  return frame.file;
}

export function revokeFrameObjectUrl(url: string | null | undefined) {
  if (url?.startsWith('blob:') && ![...frameCache.values()].includes(url)) {
    URL.revokeObjectURL(url);
  }
}

export function formatVideoClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
